import React, { useState } from 'react';
import { Search, Plus, AlertTriangle, ThumbsUp, ThumbsDown, Edit, Trash2, ExternalLink } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { mockUnlistedFirms } from '@/data/mock';
import { toast } from 'sonner';

export const UnlistedFirmsPage = () => {
  const [firms, setFirms] = useState(mockUnlistedFirms);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const filteredFirms = firms.filter((firm) =>
    firm.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unlistedCount = firms.filter(f => f.status === 'Unlisted').length;
  const warningCount = firms.filter(f => f.status === 'Warning').length;
  const delistedCount = firms.filter(f => f.status === 'Delisted').length;

  return (
    <div>
      <PageHeader
        title="Unlisted Firms"
        description="Manage firms with warnings, delistings, or under review"
        breadcrumbs={['Admin', 'Content', 'Unlisted Firms']}
        actions={
          <Button onClick={() => setIsAddingNew(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Unlisted Firm
          </Button>
        }
      />

      <div className="grid gap-6 md:grid-cols-3 mb-6">
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Unlisted</p>
                <p className="text-3xl font-bold tracking-tight text-slate-900 mt-2">{unlistedCount}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <AlertTriangle className="h-6 w-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Warning</p>
                <p className="text-3xl font-bold tracking-tight text-slate-900 mt-2">{warningCount}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Delisted</p>
                <p className="text-3xl font-bold tracking-tight text-slate-900 mt-2">{delistedCount}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search unlisted firms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Firm</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reason</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date Flagged</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Community Votes</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFirms.map((firm) => (
                  <TableRow key={firm.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img src={firm.logo} alt="" className="h-10 w-10 rounded" />
                        <span className="text-sm font-medium text-slate-900">{firm.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={firm.status} />
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-600 line-clamp-2 max-w-md">
                        {firm.reasonSummary}
                      </p>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {format(new Date(firm.dateFlagged), 'MMM dd, yyyy')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <ThumbsUp className="h-3 w-3 text-emerald-600" />
                          <span className="text-sm font-medium">{firm.communityVotes.trust}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ThumbsDown className="h-3 w-3 text-red-600" />
                          <span className="text-sm font-medium">{firm.communityVotes.distrust}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedFirm(firm)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Sheet open={isAddingNew || !!selectedFirm} onOpenChange={(open) => { if (!open) { setIsAddingNew(false); setSelectedFirm(null); }}}>
        <SheetContent className="w-[500px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{isAddingNew ? 'Add Unlisted Firm' : 'Edit Unlisted Firm'}</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div>
              <Label>Firm Name</Label>
              <Input placeholder="Enter firm name" className="mt-2" />
            </div>
            <div>
              <Label>Status</Label>
              <RadioGroup defaultValue="unlisted" className="mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="unlisted" id="unlisted" />
                  <Label htmlFor="unlisted">Unlisted</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="warning" id="warning" />
                  <Label htmlFor="warning">Warning</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="delisted" id="delisted" />
                  <Label htmlFor="delisted">Delisted</Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label>Reason / Summary</Label>
              <Textarea
                placeholder="Describe the reason for flagging this firm..."
                className="mt-2"
                rows={4}
              />
            </div>
            <div>
              <Label>Source Links</Label>
              <Input placeholder="https://..." className="mt-2" />
              <Button variant="outline" size="sm" className="mt-2">
                <Plus className="h-3 w-3 mr-1" />
                Add Link
              </Button>
            </div>
            <Button className="w-full" onClick={() => { toast.success('Firm saved'); setIsAddingNew(false); setSelectedFirm(null); }}>
              Save
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};